import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Button
} from "reactstrap";

import {
    RiBookOpenLine,
    RiMusic2Line
} from "react-icons/ri";

import Api from "../../components/helpers/Api";
import { SeoHelper } from "../../components/helpers/SeoHelper";
import { buildScriptureSEO } from "../../utils/buildScriptureSEO";

export default function ScriptureChapterPage() {

    const { book, chapter } = useParams();

    const [verses, setVerses] = useState([]);
    const [songs, setSongs] = useState([]);
    const [bookMeta, setBookMeta] = useState(null);
    const [popular, setPopular] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});

    const GROUP_VISIBLE_LIMIT = 5;

    // ==========================
    // ✅ SEO (DETERMINISTIC)
    // ==========================
    const seo = buildScriptureSEO({
        book,
        chapter,
        verseStart: null,
        verseEnd: null
    });

    // ==========================
    // DATA (UI ONLY)
    // ==========================
    useEffect(() => {
        Api.get("/api/testament/books")
            .then(res => {
                const found = res.data.find(b => b.slug === book);
                setBookMeta(found);
            });
    }, [book]);

    useEffect(() => {
        Api.get(`/api/scripture-songs/${book}/${chapter}`)
            .then(res => {
                if (res.data?.songs) {
                    setSongs(res.data.songs);
                }
            });
    }, [book, chapter]);

    useEffect(() => {
        Api.get(`/api/scripture-songs/${book}/${chapter}/verses`)
            .then(res => setVerses(res.data || []));
    }, [book, chapter]);

    useEffect(() => {
        Api.get("/api/scripture-songs/popular")
            .then(res => setPopular(res.data));
    }, []);

    useEffect(() => {
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'view_scripture', {
                scripture_reference: `${formatBookName(book)} ${chapter}`,
                source: 'browse'
            });
        }
    }, [book, chapter]);

    function formatBookName(value) {
        if (!value) return "";

        return value
            .replace(/-/g, " ")
            .split(" ")
            .filter(Boolean)
            .map(p => /^\d+$/.test(p) ? p : p.charAt(0).toUpperCase() + p.slice(1))
            .join(" ");
    }

    const startPlanning = () => {
        if (!bookMeta) return;

        const passage = {
            bookId: bookMeta.bookId,
            book: bookMeta.title,
            chapter: parseInt(chapter)
        };

        sessionStorage.setItem(
            "selected-passages",
            JSON.stringify([passage])
        );

        window.location.href = "/scripture/choose-passages";
    };

    return (
        <>
            {/* ✅ ALWAYS RENDER SEO */}
            <SeoHelper
                uri={seo.canonical}
                title={seo.title}
                description={seo.description}
            />

            {!bookMeta ? (
                <div className="text-center mt-5">Loading...</div>
            ) : (
                <section className="section section-lg">

                    <Container>

                        <Row className="justify-content-center">

                            <Col lg="8">

                                <h1 className="display-4 text-center text-primary mb-4">
                                    {seo.heading}
                                </h1>

                                <p className="lead text-center text-muted mb-5">
                                    Explore verses from {bookMeta.title} {chapter} and discover worship songs connected to each verse.
                                </p>

                                {songs.length > 0 && (
                                    <Card className="mb-4">
                                        <CardBody>
                                            <h2 className="h4 mb-3 text-center">
                                                <RiMusic2Line size={22} style={{ marginRight: "8px" }} />
                                                Recommended Worship Songs
                                            </h2>

                                            <ul className="twb-grid">
                                                {songs.map(song => (
                                                    <li key={song.id}>
                                                        <div className="twb-next-step">
                                                            <div className="twb-next-step-icon">
                                                                <RiMusic2Line size={20} />
                                                            </div>
                                                            <div className="twb-next-step-text">
                                                                {song.title}
                                                            </div>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        </CardBody>
                                    </Card>
                                )}

                                {verses.length > 0 ? (
                                    <Card>
                                        <CardBody>
                                            <h2 className="h4 text-center mb-4">
                                                Verses in {bookMeta.title} {chapter}
                                            </h2>

                                            <ul className="twb-grid">
                                                {verses.map(v => (
                                                    <li key={v.url}>
                                                        <Link to={v.url} className="twb-next-step">
                                                            <div className="twb-next-step-icon">
                                                                <RiBookOpenLine size={22} />
                                                            </div>
                                                            <div className="twb-next-step-text">
                                                                Verse&nbsp;{v.verseFrom}
                                                            </div>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </CardBody>
                                    </Card>
                                ) : (
                                    <p className="text-muted">
                                        There are currently no songs for this chapter.
                                    </p>
                                )}

                                <Card className="mb-4 mt-4">
                                    <CardBody className="text-center">
                                        <h3 className="h5 mb-3">
                                            Plan a Worship Service Around {seo.heading.replace("Worship Songs for ", "")}
                                        </h3>

                                        <Button color="primary" onClick={startPlanning}>
                                            Start Planning a Song-Flow
                                        </Button>
                                    </CardBody>
                                </Card>

                            </Col>

                        </Row>

                    </Container>

                </section>
            )}
        </>
    );
}
